(() => {
  if (window.__aqkJudgeMeReviewBridge) return;
  window.__aqkJudgeMeReviewBridge = true;

  const rootSelector = '[data-aqk-judgeme-reviews]';
  const triggerSelector = '[data-aqk-review-open]';
  const reviewHash = '#judgeme_product_reviews';
  const readyTimeout = 8000;
  const pollInterval = 125;

  const normaliseJudgeMeMediaMarkup = (root = document) => {
    root.querySelectorAll('.jm-media-grid__container').forEach((container) => {
      if (container.getAttribute('role') !== 'tablist') container.setAttribute('role', 'tablist');
      if (!container.hasAttribute('aria-label')) container.setAttribute('aria-label', 'Customer review media');

      container.querySelectorAll('button.jm-media-grid__item').forEach((button) => {
        if (button.getAttribute('role') !== 'tab') button.setAttribute('role', 'tab');
      });
    });

    root.querySelectorAll('.jm-cluster .jm-frame img').forEach((image) => {
      if (!image.hasAttribute('width')) image.setAttribute('width', '320');
      if (!image.hasAttribute('height')) image.setAttribute('height', '320');
    });
  };

  const isVisible = (element) => {
    if (!(element instanceof HTMLElement)) return false;
    if (element.hidden || element.getAttribute('aria-hidden') === 'true') return false;
    if (element.matches(':disabled, [aria-disabled="true"]')) return false;

    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
  };

  const findNativeReviewControl = (root) => {
    const knownSelectors = [
      '.jdgm-write-rev-link',
      '.jm-write-review-button',
      '[data-jdgm-write-review]',
      '[data-testid="write-review-button"]'
    ];

    for (const selector of knownSelectors) {
      const candidate = root.querySelector(selector);
      if (candidate && !candidate.matches(triggerSelector) && isVisible(candidate)) return candidate;
    }

    return Array.from(root.querySelectorAll('button, a, [role="button"]')).find((candidate) => {
      if (candidate.matches(`${triggerSelector}, [data-aqk-review-fallback]`)) return false;
      const label = `${candidate.getAttribute('aria-label') || ''} ${candidate.textContent || ''}`
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
      return label.includes('write a review') && isVisible(candidate);
    }) || null;
  };

  const setStatus = (root, message = '', state = '') => {
    const status = root.querySelector('[data-aqk-review-status]');
    if (!status) return;

    status.textContent = message;
    status.hidden = message.length === 0;
    if (state) status.dataset.state = state;
    else delete status.dataset.state;
  };

  const setFallbackVisibility = (root, visible) => {
    const fallback = root.querySelector('[data-aqk-review-fallback]');
    if (fallback) fallback.hidden = !visible;
  };

  const waitForNativeReviewControl = (root) => new Promise((resolve) => {
    const immediate = findNativeReviewControl(root);
    if (immediate) {
      resolve(immediate);
      return;
    }

    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      const candidate = findNativeReviewControl(root);
      if (candidate || Date.now() - startedAt >= readyTimeout) {
        window.clearInterval(timer);
        resolve(candidate || null);
      }
    }, pollInterval);
  });

  const openReviewForm = async (root, trigger) => {
    if (!root || root.dataset.aqkReviewOpening === 'true') return;

    root.dataset.aqkReviewOpening = 'true';
    trigger?.setAttribute('aria-busy', 'true');
    if (trigger instanceof HTMLButtonElement) trigger.disabled = true;
    setFallbackVisibility(root, false);
    setStatus(root, 'Loading the secure Judge.me review form…');

    const nativeControl = await waitForNativeReviewControl(root);

    root.dataset.aqkReviewOpening = 'false';
    trigger?.removeAttribute('aria-busy');
    if (trigger instanceof HTMLButtonElement) trigger.disabled = false;

    if (!nativeControl) {
      setStatus(
        root,
        'The secure review form did not load. Try again, refresh the page, or use the help option below.',
        'error'
      );
      setFallbackVisibility(root, true);
      return;
    }

    setFallbackVisibility(root, false);
    setStatus(root, 'Opening the secure Judge.me review form…');
    nativeControl.click();

    window.setTimeout(() => {
      setStatus(root, 'If the form did not open, select “Write a review” again.');
    }, 900);
  };

  const getReviewRoot = () => document.querySelector(rootSelector);

  document.addEventListener('click', (event) => {
    const directTrigger = event.target.closest?.(triggerSelector);
    if (directTrigger) {
      event.preventDefault();
      openReviewForm(directTrigger.closest(rootSelector), directTrigger);
      return;
    }

    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const reviewLink = event.target.closest?.('a[href]');
    if (!reviewLink || reviewLink.classList.contains('jdgm-write-rev-link')) return;

    const destination = new URL(reviewLink.href, window.location.href);
    if (
      destination.origin !== window.location.origin ||
      destination.pathname !== window.location.pathname ||
      destination.hash !== reviewHash
    ) {
      return;
    }

    const root = getReviewRoot();
    if (!root) return;

    event.preventDefault();
    if (window.location.hash !== reviewHash) history.pushState(null, '', reviewHash);
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    openReviewForm(root, root.querySelector(triggerSelector));
  });

  const prepareLinkedReviewSection = () => {
    if (window.location.hash !== reviewHash) return;
    const root = getReviewRoot();
    const trigger = root?.querySelector(triggerSelector);
    if (!root || !trigger) return;

    window.requestAnimationFrame(() => {
      root.scrollIntoView({ block: 'start' });
      trigger.focus({ preventScroll: true });
      setStatus(root, 'Select “Write a review” to open the secure Judge.me form.');
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      prepareLinkedReviewSection();
      normaliseJudgeMeMediaMarkup();
    }, { once: true });
  } else {
    prepareLinkedReviewSection();
    normaliseJudgeMeMediaMarkup();
  }

  const judgeMeRoot = getReviewRoot();
  if (judgeMeRoot && 'MutationObserver' in window) {
    new MutationObserver(() => normaliseJudgeMeMediaMarkup(judgeMeRoot)).observe(judgeMeRoot, {
      childList: true,
      subtree: true
    });
  }

  window.addEventListener('hashchange', prepareLinkedReviewSection);
})();
